import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Save, Building2, Star, Sparkles, Image as ImageIcon, FileText, Plus, X, MapPin, Upload, Share2, Copy, Trash2, Eye, QrCode, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const PROPERTY_TYPES = ["Homestays", "Villa", "Apartment", "Boutique Hotel", "Guest House", "Resort", "Hotel", "Bed & Breakfast", "Hostel"];
const STARS = ["Unrated", "3 stars & above", "4 stars & above", "5 stars & above"];
const COUNTRIES = ["India", "Nepal", "Sri Lanka", "Thailand", "Vietnam", "UAE"];
const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40";

const SUB_TABS = [
  { id: "profile", label: "Profile", icon: Building2 },
  { id: "highlights", label: "Highlights", icon: Star },
  { id: "amenities", label: "Amenities", icon: Sparkles },
  { id: "photos", label: "Photo Gallery", icon: ImageIcon },
  { id: "policies", label: "Policies", icon: FileText },
  { id: "qr", label: "Property QR", icon: QrCode },
];

const AMENITY_CATALOG = [
  "Wi-Fi", "Pool", "AC", "Kitchen", "Parking", "Breakfast", "Housekeeping",
  "Power backup", "TV", "Washing machine", "Pet friendly", "Wheelchair access",
  "Balcony", "Garden", "Rooftop", "Gym", "Spa", "In-unit dining", "Airport transfer",
  "24/7 reception", "Laundry", "BBQ", "Bonfire", "Bar", "Restaurant",
];

export default function PropertyProfile({ pid, propertyName }) {
  const [data, setData] = useState(null);
  const [sub, setSub] = useState("profile");
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(false);
  const [mapsKey, setMapsKey] = useState("");
  const addrInputRef = useRef(null);
  const acRef = useRef(null);
  // Item 2 (Batch 6, v2): user enabled ONLY "Places API (New)" on their key —
  // the legacy `google.maps.places.Autocomplete` widget silently returns
  // zero results in that case. Use the new AutocompleteSuggestion service +
  // a custom dropdown so we don't need the legacy API enabled at all.
  const [addrQuery, setAddrQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [showPreds, setShowPreds] = useState(false);
  const sessionRef = useRef(null);
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    setData(null); setDirty({});
    api.get(`/pms/properties/${pid}/profile`).then(({ data: d }) => setData(d))
      .catch((e) => toast.error(errMsg(e)));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  // Lazy-load Google Maps Places (New) JS once we have the key.
  useEffect(() => {
    api.get(`/pms/config/maps-key`).then(({ data: d }) => setMapsKey(d.key || "")).catch(() => {});
  }, []);
  useEffect(() => {
    if (!mapsKey || sub !== "profile" || !data) return;
    if (window.google?.maps?.importLibrary) return;
    const id = "gmaps-places-script";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&libraries=places&v=weekly&loading=async`;
    document.head.appendChild(s);
  }, [mapsKey, sub, data]);

  // Debounced fetch against the NEW Places API via AutocompleteSuggestion.
  // The user's key only has Places API (New) enabled, so the legacy
  // `Autocomplete` widget was silently returning nothing — this path uses
  // the same v1 endpoint that our curl confirmed works ("Maan Mahal by
  // Atiti Homestays…"). Session tokens keep autocomplete + detail
  // fetches on the cheap per-session billing tier.
  const fetchPredictions = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setPredictions([]); return; }
    try {
      if (!window.google?.maps?.importLibrary) return;
      const { AutocompleteSuggestion, AutocompleteSessionToken } = await window.google.maps.importLibrary("places");
      if (!sessionRef.current) sessionRef.current = new AutocompleteSessionToken();
      const r = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: q, sessionToken: sessionRef.current,
      });
      setPredictions((r.suggestions || []).slice(0, 6));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Places autocomplete failed:", e);
      setPredictions([]);
    }
  }, []);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(addrQuery), 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [addrQuery, fetchPredictions]);

  const pickPrediction = async (sugg) => {
    try {
      const place = sugg.placePrediction.toPlace();
      await place.fetchFields({ fields: ["addressComponents", "formattedAddress", "location", "displayName"] });
      const comps = place.addressComponents || [];
      const pick = (t) => comps.find((c) => (c.types || []).includes(t))?.longText || "";
      const street = [pick("street_number"), pick("route")].filter(Boolean).join(" ");
      setData((prev) => ({
        ...prev,
        address_1: street || place.formattedAddress || prev.address_1 || "",
        address_2: [pick("sublocality_level_1"), pick("sublocality")].filter(Boolean).join(", "),
        city: pick("locality") || pick("administrative_area_level_2") || prev.city || "",
        state: pick("administrative_area_level_1") || prev.state || "",
        postal_code: pick("postal_code") || prev.postal_code || "",
        country: pick("country") || prev.country || "India",
        lat: place.location?.lat?.() ?? prev.lat,
        lng: place.location?.lng?.() ?? prev.lng,
      }));
      setDirty((x) => ({ ...x, address_1: true, address_2: true, city: true, state: true, postal_code: true, country: true, lat: true, lng: true }));
      setAddrQuery(place.formattedAddress || sugg.placePrediction.text.text);
      setPredictions([]); setShowPreds(false);
      sessionRef.current = null; // detail-fetch closes the session
      toast.success("Address auto-filled from Google Places");
    } catch (e) {
      toast.error("Could not fetch place details");
    }
  };

  const set = (k, v) => { setData((d) => ({ ...d, [k]: v })); setDirty((x) => ({ ...x, [k]: true })); };

  const save = async () => {
    const patch = {};
    Object.keys(dirty).forEach((k) => { patch[k] = data[k]; });
    if (!Object.keys(patch).length) return;
    setBusy(true);
    try {
      await api.put(`/pms/properties/${pid}/profile`, patch);
      toast.success(`Property configuration saved · ${Object.keys(patch).length} field${Object.keys(patch).length > 1 ? "s" : ""} · pushed to every channel`);
      setDirty({});
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  const highlights = data.highlights || [];
  const amenities = data.amenities || [];
  const photos = data.photos || [];
  const policies = data.policies || {};

  return (
    <div className="reveal space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-display font-bold text-slate-900 text-sm">Property Configuration</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">{propertyName} · every field pushes to Atiti + 150+ channels on save. Different locations → different configurations.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-semibold text-slate-500">{Object.keys(dirty).length ? `${Object.keys(dirty).length} unsaved` : "All saved"}</span>
          <button data-testid="profile-save" onClick={save} disabled={!Object.keys(dirty).length || busy}
            className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save & push
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto -mx-1 px-1 whitespace-nowrap md:flex-wrap md:overflow-visible md:whitespace-normal md:mx-0 md:px-0">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`profile-sub-${t.id}`} onClick={() => setSub(t.id)}
              className={`px-3 md:px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1.5 shrink-0 ${sub === t.id ? "border-[#4F46E5] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {sub === "profile" && (
        <div className="space-y-5">
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 text-sm mb-4">General Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-1">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Hotel logo</label>
                {data.logo_url ? (
                  <div className="mt-1 h-32 w-full rounded-lg border border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center">
                    <img src={data.logo_url} alt="logo" className="max-h-full max-w-full object-contain" />
                  </div>
                ) : (
                  <div className="mt-1 h-32 w-full rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 text-xs">No logo</div>
                )}
                <LogoUploader pid={pid} onUploaded={(u) => set("logo_url", u)} />
              </div>
              <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                <F label="Property Name *"><input data-testid="profile-name" value={data.name || ""} onChange={(e) => set("name", e.target.value)} className={inp} /></F>
                <F label="Property Type"><select data-testid="profile-type" value={data.type || ""} onChange={(e) => set("type", e.target.value)} className={inp}><option value="">Select…</option>{PROPERTY_TYPES.map((p) => <option key={p}>{p}</option>)}</select></F>
                <F label="Star Rating"><select data-testid="profile-stars" value={data.star_rating || ""} onChange={(e) => set("star_rating", e.target.value)} className={inp}><option value="">—</option>{STARS.map((s) => <option key={s}>{s}</option>)}</select></F>
                <F label="Official Email *"><input data-testid="profile-email" type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} className={inp} /></F>
                <F label="Primary Contact *"><input data-testid="profile-phone" value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inp} /></F>
                <F label="Reservation Contact"><input value={data.res_phone || ""} onChange={(e) => set("res_phone", e.target.value)} className={inp} /></F>
                <F label="Property Website"><input value={data.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className={inp} /></F>
                <F label="GSTIN / Registration No."><input data-testid="profile-gstin" value={data.reg_number || ""} onChange={(e) => set("reg_number", e.target.value)} placeholder="10ABCCA3526H1ZA" className={inp} /></F>
                {[0, 1, 2, 3].map((i) => (
                  <F key={i} label={`Additional Reg. ${i + 1}`}>
                    <input value={(data.additional_regs || [])[i] || ""} onChange={(e) => { const a = [...(data.additional_regs || ["", "", "", ""])]; a[i] = e.target.value; set("additional_regs", a); }} className={inp} />
                  </F>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 text-sm mb-4 flex items-center gap-2"><MapPin size={14} className="brand-pink" /> Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label="Search address · Google Places" span2>
                <div className="relative">
                  <input
                    ref={addrInputRef}
                    data-testid="profile-addr-search"
                    value={addrQuery}
                    onChange={(e) => { setAddrQuery(e.target.value); setShowPreds(true); }}
                    onFocus={() => setShowPreds(true)}
                    onBlur={() => setTimeout(() => setShowPreds(false), 200)}
                    placeholder={mapsKey ? "Start typing an address or business name…" : "Loading Places…"}
                    className={`${inp} bg-indigo-50/40 border-indigo-200`}
                    autoComplete="off"
                  />
                  {showPreds && predictions.length > 0 && (
                    <div data-testid="profile-addr-suggestions" className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                      {predictions.map((s, i) => {
                        const main = s.placePrediction?.structuredFormat?.mainText?.text || s.placePrediction?.text?.text || "";
                        const sec = s.placePrediction?.structuredFormat?.secondaryText?.text || "";
                        return (
                          <button key={s.placePrediction?.placeId || i} type="button"
                            data-testid={`profile-addr-sugg-${i}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickPrediction(s)}
                            className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-b-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">{main}</div>
                            {sec && <div className="text-[11px] text-slate-500 truncate">{sec}</div>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  {mapsKey
                    ? "Pick a suggestion to auto-fill line 1, city, state, postal code, country and coordinates."
                    : "Google Places key not set — fill address fields manually below."}
                </p>
              </F>
              <F label="Street Address (Line 1) *"><input data-testid="profile-addr1" value={data.address_1 || ""} onChange={(e) => set("address_1", e.target.value)} className={inp} /></F>
              <F label="Street Address (Line 2)"><input value={data.address_2 || ""} onChange={(e) => set("address_2", e.target.value)} className={inp} /></F>
              <F label="Country *"><select value={data.country || "India"} onChange={(e) => set("country", e.target.value)} className={inp}>{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select></F>
              <F label="State / Province"><input value={data.state || ""} onChange={(e) => set("state", e.target.value)} className={inp} /></F>
              <F label="City / Town *"><input value={data.city || ""} onChange={(e) => set("city", e.target.value)} className={inp} /></F>
              <F label="Postal / Zip Code"><input value={data.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} className={inp} /></F>
              <F label="Latitude Coordinates"><input data-testid="profile-lat" type="number" step="0.000001" value={data.lat ?? ""} onChange={(e) => set("lat", parseFloat(e.target.value))} className={`${inp} font-mono`} /></F>
              <F label="Longitude Coordinates"><input data-testid="profile-lng" type="number" step="0.000001" value={data.lng ?? ""} onChange={(e) => set("lng", parseFloat(e.target.value))} className={`${inp} font-mono`} /></F>
            </div>
            {data.lat && data.lng && (
              <div className="mt-4 h-72 w-full rounded-lg overflow-hidden border border-slate-200">
                <iframe title="map" width="100%" height="100%" frameBorder="0" style={{ border: 0 }}
                  src={`https://maps.google.com/maps?q=${data.lat},${data.lng}&z=15&output=embed`} />
              </div>
            )}
          </section>
        </div>
      )}

      {sub === "highlights" && (
        <ListEditor title="Property Highlights" placeholder="e.g. Ocean-facing rooftop pool" testid="highlights" items={highlights} onChange={(a) => set("highlights", a)} help="Short marketing bullets shown to guests on Atiti and OTAs. Keep each under 60 characters." />
      )}
      {sub === "amenities" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 text-sm mb-2">Amenities</h3>
          <p className="text-[11px] text-slate-500 mb-4">Click to toggle. Selected amenities push into every channel listing.</p>
          <div className="flex flex-wrap gap-2">
            {AMENITY_CATALOG.map((a) => {
              const active = amenities.includes(a);
              return (
                <button key={a} data-testid={`amenity-${a}`}
                  onClick={() => set("amenities", active ? amenities.filter((x) => x !== a) : [...amenities, a])}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                  {a}
                </button>
              );
            })}
          </div>
          <p className="mt-4 text-[11px] text-slate-500 font-semibold">{amenities.length} selected</p>
        </div>
      )}
      {sub === "photos" && (
        <PhotoGalleryTab pid={pid} photos={photos} propertyName={data.name}
          onChange={(a) => set("photos", a)} />
      )}
      {sub === "policies" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h3 className="font-display font-bold text-slate-900 text-sm">Policies</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Check-in time"><input type="time" value={policies.checkin_time || "14:00"} onChange={(e) => set("policies", { ...policies, checkin_time: e.target.value })} className={inp} /></F>
            <F label="Check-out time"><input type="time" value={policies.checkout_time || "11:00"} onChange={(e) => set("policies", { ...policies, checkout_time: e.target.value })} className={inp} /></F>
            <F label="Currency"><input value={policies.currency || "INR"} onChange={(e) => set("policies", { ...policies, currency: e.target.value })} className={inp} /></F>
          </div>
          <F label="Cancellation policy"><textarea rows="3" value={policies.cancellation || ""} onChange={(e) => set("policies", { ...policies, cancellation: e.target.value })} placeholder="Free cancellation up to 48h before check-in…" className={inp} /></F>
          <F label="House rules"><textarea rows="3" value={policies.house_rules || ""} onChange={(e) => set("policies", { ...policies, house_rules: e.target.value })} placeholder="No smoking, pets on request…" className={inp} /></F>
          <F label="Terms & conditions"><textarea rows="3" value={policies.terms || ""} onChange={(e) => set("policies", { ...policies, terms: e.target.value })} className={inp} /></F>
        </div>
      )}
      {sub === "qr" && (
        <PropertyQrTab pid={pid} propertyName={data.name}
          googleReviewUrl={data.google_review_url || ""}
          onSetReview={(v) => set("google_review_url", v)} />
      )}
    </div>
  );
}

// Item 3 (Batch 6): Photo Gallery tab — real uploads, in-place viewer &
// shareable public link. Persists to /pms/properties/{pid}/photos and mirrors
// results into `data.photos` so the existing Save flow + OTA/voucher
// bindings keep working unchanged (photos remain `list[str]` under the hood).
function PhotoGalleryTab({ pid, photos, propertyName, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const remaining = 30 - photos.length;
  const shareUrl = `${window.location.origin}/gallery/${pid}`;

  const pick = () => fileRef.current?.click();
  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (files.length > remaining) {
      toast.error(`Only ${remaining} slot(s) left — remove some photos or select fewer.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post(`/pms/properties/${pid}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.photos);
      toast.success(`${r.data.added.length} photo(s) uploaded · ${r.data.remaining} slots left`);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const del = async (url) => {
    try {
      const r = await api.delete(`/pms/properties/${pid}/photos`, { data: { url } });
      onChange(r.data.photos);
      toast.success("Photo removed");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Public gallery link copied");
    } catch { toast.info(shareUrl); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="photo-gallery-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-slate-900 text-sm">Photo Gallery</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Up to 30 photos · PNG/JPG/WEBP ≤5 MB. First photo is the hero on every channel and the voucher.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-slate-500">{photos.length}/30</span>
          <button data-testid="gallery-view-btn" onClick={() => photos.length && setLightbox(0)} disabled={!photos.length}
            className="text-xs font-semibold text-slate-600 border border-slate-200 rounded-full px-3 py-1.5 flex items-center gap-1 hover:border-slate-400 disabled:opacity-40">
            <Eye size={12} /> View
          </button>
          <button data-testid="gallery-share-btn" onClick={copyShare} disabled={!photos.length}
            className="text-xs font-semibold text-[#4F46E5] border border-indigo-200 rounded-full px-3 py-1.5 flex items-center gap-1 hover:bg-indigo-50 disabled:opacity-40">
            <Share2 size={12} /> Copy share link
          </button>
          <button data-testid="gallery-upload-btn" onClick={pick} disabled={busy || remaining === 0}
            className="btn-brand rounded-full px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />} Upload photos
          </button>
        </div>
      </div>
      <input ref={fileRef} data-testid="gallery-file" type="file" multiple
        accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />

      {photos.length === 0 ? (
        <button onClick={pick} className="w-full py-16 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:border-[#4F46E5] hover:text-[#4F46E5] hover:bg-indigo-50 text-sm font-semibold">
          <Upload size={22} className="mx-auto mb-2" /> Click to upload the first photo (up to 30)
        </button>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
          {photos.map((url, i) => (
            <div key={url} data-testid={`gallery-photo-${i}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200 group">
              <img src={url} alt="" className="w-full h-full object-cover cursor-pointer"
                onClick={() => setLightbox(i)} />
              {i === 0 && <span className="absolute top-1 left-1 bg-[#4F46E5] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">Hero</span>}
              <button data-testid={`gallery-delete-${i}`} onClick={() => del(url)}
                className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 hover:text-red-600 text-slate-500 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
        <Copy size={12} /> Public gallery link:
        <code className="font-mono text-[11px] bg-slate-100 px-2 py-0.5 rounded">{shareUrl}</code>
      </div>

      {lightbox !== null && (
        <div className="fixed inset-0 bg-slate-900/90 z-[90] flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white" onClick={() => setLightbox(null)}><X size={24} /></button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + photos.length) % photos.length); }}>‹</button>
          <img src={photos[lightbox]} alt="" className="max-h-[85vh] max-w-[85vw] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % photos.length); }}>›</button>
          <p className="absolute bottom-4 left-0 right-0 text-center text-white/70 text-xs">{propertyName} · {lightbox + 1} / {photos.length}</p>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children, span2 }) => (
  <div className={span2 ? "md:col-span-2" : ""}>
    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const ListEditor = ({ title, help, placeholder, testid, items, onChange, isImage }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-3">
      <div>
        <h3 className="font-display font-bold text-slate-900 text-sm">{title}</h3>
        {help && <p className="text-[11px] text-slate-500 mt-0.5">{help}</p>}
      </div>
      <button data-testid={`${testid}-add`} onClick={() => onChange([...items, ""])} className="text-xs text-[#4F46E5] font-semibold flex items-center gap-1"><Plus size={12} /> Add</button>
    </div>
    {items.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">Nothing yet — click Add.</p>}
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          {isImage && it && <img src={it} alt="" className="w-10 h-10 rounded object-cover border border-slate-200" onError={(e) => e.target.style.opacity = 0.2} />}
          <span className="font-mono text-[10px] text-slate-400 w-5">{i + 1}</span>
          <input data-testid={`${testid}-input-${i}`} value={it} onChange={(e) => onChange(items.map((v, j) => j === i ? e.target.value : v))} placeholder={placeholder} className={`${inp} flex-1`} />
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500"><X size={14} /></button>
        </div>
      ))}
    </div>
  </div>
);

// Item 1 (Batch 6): Replaces the previous logo URL text input in the Profile
// tab with a real image uploader. Persists via /profile/logo, then hands the
// resulting public URL to the parent's `logo_url` field so the existing
// Save flow, voucher and invoice bindings all keep working unchanged.
function LogoUploader({ pid, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => ref.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error("Please pick a PNG, JPG or WEBP image");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/pms/properties/${pid}/profile/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(r.data.logo_url);
      toast.success("Logo uploaded");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="mt-2">
      <input ref={ref} data-testid="profile-logo-file" type="file"
        accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
      <button data-testid="profile-logo-upload-btn" onClick={pick} disabled={busy}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 hover:border-[#4F46E5] hover:bg-indigo-50 text-xs font-semibold text-slate-600 disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />}
        {busy ? "Uploading…" : "Upload logo (PNG/JPG, ≤2 MB)"}
      </button>
    </div>
  );
}

// Item 5 (Batch 6): Property QR tab — generate a tokenized self-serve URL
// (guest self check-in + visitor pass + Google Review). Reuses the existing
// check-in flow (#26) and Guest Database (#31) via a tokenized public route.
function PropertyQrTab({ pid, propertyName, googleReviewUrl, onSetReview }) {
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(googleReviewUrl || "");
  const [savingReview, setSavingReview] = useState(false);
  const svgRef = useRef(null);

  useEffect(() => {
    api.get(`/pms/properties/${pid}/checkin-qr`).then(({ data }) => setQr(data))
      .catch((e) => toast.error(errMsg(e)));
  }, [pid]);
  useEffect(() => { setReview(googleReviewUrl || ""); }, [googleReviewUrl]);

  const rotate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pms/properties/${pid}/checkin-qr/rotate`);
      setQr(data); toast.success("QR generated · new link is live");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const saveReview = async () => {
    setSavingReview(true);
    try {
      await api.put(`/pms/properties/${pid}/profile`, { google_review_url: review.trim() });
      onSetReview?.(review.trim());
      toast.success("Google Review URL saved");
    } catch (e) { toast.error(errMsg(e)); }
    setSavingReview(false);
  };
  const url = qr?.token ? `${window.location.origin}/checkin/${pid}/${qr.token}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.info(url); }
  };
  const downloadPng = () => {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return;
    const s = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = 640; c.height = 640;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 640, 640);
      ctx.drawImage(img, 20, 20, 600, 600);
      const a = document.createElement("a");
      a.href = c.toDataURL("image/png"); a.download = `${propertyName || "property"}-qr.png`;
      a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(s);
  };

  if (!qr) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-300" size={26} /></div>;

  return (
    <div className="space-y-5" data-testid="qr-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-display font-bold text-slate-900 text-sm">Guest QR — self check-in · visitor pass · reviews</h3>
        <p className="text-[11px] text-slate-500 mt-1">
          Print or display this QR at your reception. Guests scan to check in themselves (reuses the same ID-verified flow #26),
          register visitors (feeds the Guest Database #31), or leave a Google Review — no app install needed.
        </p>

        {qr.token ? (
          <div className="mt-5 grid md:grid-cols-2 gap-6 items-start">
            <div ref={svgRef} className="bg-white border border-slate-200 rounded-xl p-6 flex items-center justify-center" data-testid="qr-svg-wrap">
              <QRCodeSVG value={url} size={240} level="M" marginSize={2} />
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Public link</label>
                <div className="mt-1 flex items-center gap-2">
                  <input readOnly value={url} data-testid="qr-url" className={`${inp} font-mono text-[11px]`} />
                  <button data-testid="qr-copy" onClick={copy} className="btn-brand rounded-full px-3 py-2 text-xs font-semibold flex items-center gap-1"><Copy size={12} /> Copy</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="qr-download" onClick={downloadPng} className="bg-white border border-slate-300 hover:border-slate-500 text-slate-800 rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1"><Upload size={12} className="rotate-180" /> Download PNG</button>
                <button data-testid="qr-rotate" onClick={rotate} disabled={busy} className="bg-white border border-amber-300 hover:bg-amber-50 text-amber-800 rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1 disabled:opacity-50">
                  {busy ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Rotate token
                </button>
              </div>
              <p className="text-[11px] text-slate-500">
                Rotating invalidates the previous QR — reprint after rotating. Last rotated:
                <span className="font-mono ml-1">{qr.rotated_at ? new Date(qr.rotated_at).toLocaleString() : "—"}</span>
              </p>
            </div>
          </div>
        ) : (
          <button data-testid="qr-generate" onClick={rotate} disabled={busy}
            className="mt-5 btn-brand rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <QrCode size={14} />} Generate Property QR
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-display font-bold text-slate-900 text-sm">Google Review URL</h3>
        <p className="text-[11px] text-slate-500 mt-1">
          Paste the "Write a review" link for your Google Business Profile. The QR page shows a
          <b> Leave a Google Review </b> button that opens this link directly.
        </p>
        <div className="mt-4 flex items-center gap-2">
          <input data-testid="qr-review-url" value={review} onChange={(e) => setReview(e.target.value)}
            placeholder="https://g.page/r/...  or  https://search.google.com/local/writereview?..."
            className={inp} />
          <button data-testid="qr-review-save" onClick={saveReview} disabled={savingReview}
            className="btn-brand rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1 disabled:opacity-50">
            {savingReview ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
